import React from 'react';
import { Service, AddOn, Venue, CheckoutPricing } from '@/types';

interface OrderSummaryProps {
  selectedServices: Service[];
  selectedAddOnIds: string[];
  addOnsById: Map<string, AddOn>;
  venue: Venue | undefined;
  pricing: CheckoutPricing;
  getPrice: (item: Service | AddOn) => number | string;
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedServices,
  selectedAddOnIds,
  addOnsById,
  venue,
  pricing,
  getPrice
}) => (
  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 h-fit">
    <h3 className="font-bold text-lg mb-4 text-slate-800 border-b pb-2">Order Summary</h3>
    <div className="space-y-4 text-sm">
      <div>
        <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest">
          {selectedServices.length > 1 ? 'Services' : 'Service'}
        </span>
        {selectedServices.map((service) => (
          <div key={service.id} className="flex justify-between mt-1">
            <span className="font-bold text-slate-900">{service.name}</span>
            <span className="font-black text-slate-700">£{getPrice(service)}</span>
          </div>
        ))}
      </div>
      {selectedAddOnIds.length > 0 && (
        <div>
          <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest mb-1">
            Add-ons
          </span>
          {selectedAddOnIds.map((id) => {
            const ad = addOnsById.get(id);
            return (
              <div key={id} className="flex justify-between mt-1 text-xs">
                <span className="text-slate-600">{ad?.name}</span>
                <span className="font-bold text-slate-700">£{ad ? getPrice(ad) : 0}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="pt-4 border-t border-slate-200">
        <div>
          <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest">
            Location
          </span>
          <span className="font-bold text-slate-900">{venue?.name}</span>
          <p className="text-xs text-slate-400">{venue?.address}</p>
        </div>
      </div>
      <div className="pt-4 border-t border-slate-200 space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-slate-500 font-bold uppercase tracking-widest">Subtotal</span>
          <span className="font-bold text-slate-700">£{pricing.subtotalAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500 font-bold uppercase tracking-widest">
            VAT ({pricing.vatRatePercent.toFixed(2)}%)
          </span>
          <span className="font-bold text-slate-700">£{pricing.vatAmount.toFixed(2)}</span>
        </div>
      </div>
      <div className="flex justify-between pt-4 border-t border-slate-200 text-xl font-black text-slate-900 uppercase tracking-tight">
        <span>Total Value</span>
        <span className="text-teal-600">£{pricing.totalAmount.toFixed(2)}</span>
      </div>
    </div>
  </div>
);
